import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getOppGroup, getPlannerEvents, reorderShowLeads, updateOppGroup, type PlannerEvent } from './crmApi'
import { OPP_GROUP_TYPES, STAGE_MAP, oppGroupType } from './crmData'
import { Avatar, CloseButton, IconCalendarUi, Kpi, Overlay, SearchInput, StageBadge, CrmToaster, crmToast } from './crmUi'
import { Button } from '../shared/Button'
import { SkeletonPage } from '../shared/Skeleton'
import { LeadKebab } from './LeadKebab'
import { TouchDots } from './DealTouchpoints'
import { getTouchpointsSummary, type TouchSummary } from './crmApi'

/**
 * /crm/shows/:id — a show/event as a PROPER SCREEN (Lee, 17 Sep 2026), not a
 * pop-up. Unmistakably "you are looking at THIS show": named header with type,
 * dates, location and the planner's money KPIs, then the captured leads as a
 * clean table (stage filter + search). Rows open the full lead SCREEN.
 */

const gbp = (n: number) => `£${Math.round(n).toLocaleString()}`
const fmtDate = (d: string | null | undefined) => {
  if (!d) return null
  const p = new Date(String(d).slice(0, 10) + 'T00:00:00')
  return isNaN(p.getTime()) ? null : p.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

export const CrmShowDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [group, setGroup] = useState<any | null>(null)
  const [opps, setOpps] = useState<any[]>([])
  const [planner, setPlanner] = useState<PlannerEvent | null>(null)
  const [q, setQ] = useState('')
  const [stageSel, setStageSel] = useState('')
  const [editing, setEditing] = useState(false)
  const [touch, setTouch] = useState<Record<string, TouchSummary>>({})
  useEffect(() => { getTouchpointsSummary().then(setTouch).catch(() => {}) }, [id])
  // Drag-to-rearrange (Lee, 17 Sep 2026). Only meaningful on the unfiltered
  // list — with a search/stage filter on, "position" is ambiguous.
  const [dragId, setDragId] = useState<string | null>(null)
  const oppsRef = React.useRef<any[]>([])
  useEffect(() => { oppsRef.current = opps }, [opps])
  const canDrag = !q.trim() && !stageSel
  const dragOver = (overId: string) => {
    if (!dragId || dragId === overId) return
    setOpps((prev) => {
      const from = prev.findIndex((x) => x.id === dragId)
      const to = prev.findIndex((x) => x.id === overId)
      if (from < 0 || to < 0) return prev
      const next = [...prev]
      const [moved] = next.splice(from, 1)
      next.splice(to, 0, moved)
      return next
    })
  }
  const dragEnd = () => {
    if (!dragId) return
    setDragId(null)
    reorderShowLeads(String(id), oppsRef.current.map((x) => x.id))
      .then(() => crmToast('Order saved', 'success'))
      .catch(() => crmToast('Could not save the order', 'error'))
  }

  const load = () => {
    getOppGroup(String(id)).then((r: any) => { setGroup(r.group); setOpps(r.opps || []) }).catch(() => crmToast('Could not load the show', 'error'))
    getPlannerEvents().then((r) => setPlanner(r.events.find((e) => e.groupId === id) || null)).catch(() => {})
  }
  useEffect(() => { load() }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

  const stagesPresent = useMemo(() => {
    const s = new Set<string>()
    opps.forEach((o) => { if (o.dealStage) s.add(o.dealStage) })
    return [...s]
  }, [opps])

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase()
    let list = opps
    if (needle) list = list.filter((o) => `${o.name} ${o.company || ''} ${o.email || ''} ${o.title || ''}`.toLowerCase().includes(needle))
    if (stageSel) list = list.filter((o) => (stageSel === '__none' ? !o.dealStage : o.dealStage === stageSel))
    return list
  }, [opps, q, stageSel])

  if (!group) return <SkeletonPage className="max-w-4xl" />
  const t = oppGroupType(group.type)

  return (
    <div className="h-full flex flex-col bg-surface-secondary">
      <CrmToaster />
      {/* Header — it's a SCREEN for this one show. */}
      <div className="px-6 pt-4 pb-3 bg-surface border-b border-border shrink-0">
        <button onClick={() => navigate('/crm/opportunities')} className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-txt-secondary hover:text-txt-primary mb-2" title="Back to Shows/Events">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
          Shows/Events
        </button>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-txt-primary truncate">{group.name}</h1>
            <div className="flex items-center gap-2.5 mt-1 flex-wrap text-[12px] text-txt-secondary">
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider" style={{ color: t.colour }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.colour }} />{t.label}
              </span>
              <span className="inline-flex items-center gap-1"><IconCalendarUi className="w-3.5 h-3.5" />{fmtDate(group.date) || 'No date'}{group.endDate && group.endDate !== group.date ? ` → ${fmtDate(group.endDate)}` : ''}</span>
              {group.location && <span>· {group.location}</span>}
              <button onClick={() => setEditing(true)} className="text-brand-600 hover:underline">Edit</button>
              <button onClick={() => navigate('/crm/event-planner')} className="text-brand-600 hover:underline">Open in Event Planner →</button>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Kpi label="Leads" value={planner ? planner.uniqueLeads : opps.length} />
            {planner && <Kpi label="Pipeline (yr)" value={gbp(planner.estPipelineAnnual)} tone="blue" />}
            {planner && <Kpi label="Won (yr)" value={gbp(planner.actualAnnualValue)} tone="green" />}
            {planner && <Kpi label="Spent" value={gbp(planner.spent)} />}
            {planner && <Kpi label="Net" value={`${planner.net >= 0 ? '+' : '−'}${gbp(Math.abs(planner.net))}`} tone={planner.net >= 0 ? 'green' : 'red'} />}
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="px-6 py-3 bg-surface border-b border-border shrink-0 flex items-center gap-2 flex-wrap">
        <SearchInput value={q} onChange={setQ} placeholder="Search leads at this show…" className="w-72 max-w-full" compact />
        <select
          value={stageSel}
          onChange={(e) => setStageSel(e.target.value)}
          className={`rounded-lg border bg-surface px-2 py-2 text-xs font-medium focus:outline-none ${stageSel ? 'border-brand-500 text-brand-600' : 'border-border text-txt-primary'}`}
        >
          <option value="">All stages</option>
          {stagesPresent.map((s) => <option key={s} value={s}>{STAGE_MAP[s]?.label || s}</option>)}
          <option value="__none">Not in the CRM</option>
        </select>
        <span className="ml-auto text-[12px] text-txt-tertiary">{shown.length} of {opps.length} lead{opps.length === 1 ? '' : 's'}</span>
      </div>

      {/* Leads table */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {shown.length === 0 ? (
          <p className="text-sm text-txt-tertiary p-6">{q || stageSel ? 'No leads match the filters.' : 'No leads captured at this show yet — import them from Shows/Events.'}</p>
        ) : (
          <table className="w-full text-sm bg-surface">
            <thead className="sticky top-0 z-10">
              <tr className="text-left text-[11px] uppercase tracking-wide text-txt-tertiary border-b border-border bg-surface-secondary">
                <th className="py-3 pl-4 pr-1 w-7" title={canDrag ? 'Drag rows to rearrange' : 'Clear the search/stage filter to rearrange'} />
                <th className="py-3 px-3 font-medium">Lead</th>
                <th className="py-3 px-3 font-medium">Company</th>
                <th className="py-3 px-3 font-medium">Email</th>
                <th className="py-3 px-3 font-medium">Stage</th>
                <th className="py-3 px-3 font-medium" title="Touchpoint health — LinkedIn › Mobile › Email › Mailing list › Telephone">Reach</th>
                <th className="py-3 px-6 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((o) => (
                <tr
                  key={o.id}
                  onClick={() => { if (o.convertedDealId) navigate(`/crm/lead/${o.convertedDealId}`) }}
                  draggable={canDrag}
                  onDragStart={() => canDrag && setDragId(o.id)}
                  onDragOver={(e) => { if (canDrag && dragId) { e.preventDefault(); dragOver(o.id) } }}
                  onDragEnd={dragEnd}
                  className={`border-b border-border last:border-0 align-middle ${dragId === o.id ? 'opacity-50 bg-brand-50/40' : ''} ${o.convertedDealId ? 'hover:bg-surface-hover cursor-pointer' : 'opacity-70'}`}
                  title={o.convertedDealId ? 'Open this lead' : 'Captured at the show but never became a CRM lead'}
                >
                  <td className="py-3 pl-4 pr-1" onClick={(e) => e.stopPropagation()}>
                    <span
                      className={`inline-flex items-center justify-center w-5 h-6 rounded ${canDrag ? 'cursor-grab active:cursor-grabbing text-txt-tertiary hover:text-txt-primary' : 'text-txt-tertiary/30 cursor-not-allowed'}`}
                      title={canDrag ? 'Drag to rearrange' : 'Clear the search/stage filter to rearrange'}
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5"><circle cx="9" cy="6" r="1.4" /><circle cx="15" cy="6" r="1.4" /><circle cx="9" cy="12" r="1.4" /><circle cx="15" cy="12" r="1.4" /><circle cx="9" cy="18" r="1.4" /><circle cx="15" cy="18" r="1.4" /></svg>
                    </span>
                  </td>
                  <td className="py-3 px-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <Avatar name={o.name} logoUrl={o.avatarUrl || undefined} />
                      <div className="min-w-0">
                        <p className="font-medium text-txt-primary truncate">{o.name}</p>
                        {o.title && <p className="text-xs text-txt-tertiary truncate">{o.title}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-3 text-txt-secondary truncate max-w-[220px]">{o.company || '—'}</td>
                  <td className="py-3 px-3 text-txt-secondary truncate max-w-[240px]">{o.email || '—'}</td>
                  <td className="py-3 px-3">
                    {o.dealStage ? <StageBadge stageKey={o.dealStage} /> : <span className="text-[11px] text-txt-tertiary">Not in CRM</span>}
                  </td>
                  <td className="py-3 px-3">{o.convertedDealId ? <TouchDots t={touch[o.convertedDealId]} /> : <span className="text-[10px] text-txt-tertiary">—</span>}</td>
                  <td className="py-3 px-6 text-right">
                    <span className="inline-flex items-center gap-1.5">
                      {o.convertedDealId && <span className="text-brand-600 text-xs font-semibold">Open →</span>}
                      <LeadKebab dealId={o.convertedDealId || null} dealName={o.company || o.name} email={o.email} phone={o.phone} oppId={o.id} onChanged={load} />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit — carried over from the retired Shows/Events pop-up (17 Sep 2026). */}
      {editing && <EditShowModal group={group} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load() }} />}
    </div>
  )
}

const EditShowModal: React.FC<{ group: any; onClose: () => void; onSaved: () => void }> = ({ group, onClose, onSaved }) => {
  const [name, setName] = useState(group.name || '')
  const [type, setType] = useState(group.type || 'trade_show')
  const [location, setLocation] = useState(group.location || '')
  const [date, setDate] = useState(group.date ? String(group.date).slice(0, 10) : '')
  const [endDate, setEndDate] = useState(group.endDate ? String(group.endDate).slice(0, 10) : '')
  const [notes, setNotes] = useState(group.notes || '')
  const [busy, setBusy] = useState(false)
  const field = 'w-full px-2.5 py-2 rounded-lg border border-border bg-surface text-sm outline-none focus:border-brand-500'
  const save = () => {
    if (!name.trim()) { crmToast('The show needs a name', 'error'); return }
    setBusy(true)
    updateOppGroup(group.id, { name: name.trim(), type, location: location.trim(), date: date || undefined, endDate: endDate || null, notes: notes.trim() })
      .then(() => { crmToast('Show updated', 'success'); onSaved() })
      .catch(() => crmToast('Could not save the show', 'error'))
      .finally(() => setBusy(false))
  }
  return (
    <Overlay onClose={onClose} width={460}>
      <div className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-txt-primary">Edit show</h3>
          <CloseButton onClick={onClose} />
        </div>
        <div className="space-y-3">
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Show name" className={field} />
          <div className="grid grid-cols-2 gap-3">
            <select value={type} onChange={(e) => setType(e.target.value)} className={field}>
              {OPP_GROUP_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
            <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Location" className={field} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="text-[10px] font-semibold uppercase tracking-wider text-txt-tertiary">Starts</span><input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={field} /></label>
            <label className="block"><span className="text-[10px] font-semibold uppercase tracking-wider text-txt-tertiary">Ends</span><input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={field} /></label>
          </div>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Notes" className={field} />
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button loading={busy} onClick={save}>Save</Button>
        </div>
      </div>
    </Overlay>
  )
}
